import { baseApi } from "../baseApi";

const integrationTag = "Integration";

/**
 * A company's connection to one external CRM/ERP system, as the gateway
 * returns it. Secrets are never part of this shape — the only trace of the
 * partner key is its last four characters, for support conversations.
 */
export interface Integration {
  id: number;
  provider: string;
  provider_kind: "accounting" | "crm" | null;
  status: "pending" | "active" | "revoked" | "error";
  organization_id: string | null;
  api_domain: string | null;
  scopes: string | null;
  /** Last 424/502 the connection hit — the cue to re-authorize. */
  last_error: string | null;
  last_used_at: string | null;
  connected_at: string;
  /**
   * Organizations the provider offered when it refused to guess which one to
   * connect. Empty when there is no open question. It lives on the row rather
   * than in the callback URL so the question survives a closed tab.
   */
  pending_choices: { id: string; name: string }[];
  partner_api_key_hint: string | null;
}

export interface ProviderOption {
  provider: string;
  label: string;
}

export interface ConnectPayload {
  provider: string;
  /** Paraşüt only: it cannot discover the organization the way Zoho can. */
  organization_id?: string;
}

export interface ConnectResponse {
  integration_id: number;
  provider: string;
  status: Integration["status"];
  /** Where the company admin has to go to grant consent. */
  authorize_url: string;
  info: string;
}

/**
 * One ledger record found for an address. `match` is normalised by
 * IcontConnect across every system; `customer` is the source system's own
 * record and its shape differs per system, so the UI renders it generically
 * rather than pretending to know the schema.
 */
export interface CustomerMatch {
  match: {
    contact_name: string | null;
    contact_id: string | null;
    phone: string | null;
    postal_code: string | null;
  };
  customer: Record<string, unknown>;
  /**
   * Documents belonging to this customer, keyed by type (invoices, estimates,
   * payments). Present only when the lookup asked for them, and only for types
   * the source system actually serves.
   */
  documents?: Record<string, Record<string, unknown>>;
}

export interface CustomerLookupResult {
  requestId: string;
  systems: string[];
  email: string;
  data:
    | { sourceSystem: string; matchCount?: number; matches: CustomerMatch[]; note?: string }
    | {
        resultsBySystem: Record<
          string,
          { sourceSystem: string; matchCount?: number; matches: CustomerMatch[]; note?: string }
        >;
      };
}

const integrationApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getIntegrations: builder.query<Integration[], void>({
      query: () => "/integrations",
      transformResponse: (response: Integration[] | { data: Integration[] }) =>
        Array.isArray(response) ? response : (response?.data ?? []),
      providesTags: [integrationTag],
    }),

    getIntegrationProviders: builder.query<ProviderOption[], void>({
      query: () => "/integrations/providers",
      transformResponse: (response: ProviderOption[] | { data: ProviderOption[] }) =>
        Array.isArray(response) ? response : (response?.data ?? []),
    }),

    /**
     * Opens a pending connection and hands back the consent URL. Nothing is
     * active until the provider redirects back, so the list is only worth
     * refetching after the user returns — not here.
     */
    connectIntegration: builder.mutation<ConnectResponse, ConnectPayload>({
      query: (body) => ({
        url: "/integrations/connect",
        method: "POST",
        body,
      }),
      invalidatesTags: [integrationTag],
    }),

    /**
     * Look a customer up by address. Not cached: support staff type an address,
     * read the answer and move on — a stale hit would be worse than a refetch.
     */
    lookupCustomer: builder.query<CustomerLookupResult, string>({
      query: (email) => ({
        url: '/integrations/customers',
        // Documents come back in the same round trip: a support desk that has
        // found the customer almost always wants to know what they owe next.
        params: { email, include: 'invoices,estimates,payments' },
      }),
      keepUnusedDataFor: 0,
    }),

    disconnectIntegration: builder.mutation<unknown, number>({
      query: (id) => ({
        url: `/integrations/${id}/disconnect`,
        method: "POST",
      }),
      invalidatesTags: [integrationTag],
    }),
  }),
});

export const {
  useLazyLookupCustomerQuery,
  useGetIntegrationsQuery,
  useGetIntegrationProvidersQuery,
  useConnectIntegrationMutation,
  useDisconnectIntegrationMutation,
} = integrationApi;
